import constants from "../../../../utils/Constants";
import { useSliderState } from "./SliderState";
import HotelSliderTemplate from "./templates/HotelSliderTemplate";
import PlacesTemplate from "./templates/PlacesTemplate";
import PoiMapTemplate from "./templates/PoiMapTemplate";
import ItineraryTemplate from "./templates/ItineraryTemplate";
import VideosTemplate from "./templates/VideosTemplate";
import OfferSliderTemplate from "./templates/OfferSliderTemplate";
import ShowcaseCard from "./templates/ShowcaseCard";
import ImmersiveView from "./ImmersiveView";
import ExploreDrawer from "./ExploreDrawer";
import BookingPanel from "./booking/BookingPanel";
import useBookingState from "./booking/BookingState";
import { isExpandableTemplate } from "./templates/utils/showcase";
import { AnimatePresence, motion } from "framer-motion";
import { LayoutGrid, Maximize2, X } from "lucide-react";
import localization from "../../../../assets/localization";
import { useVisualViewportBox } from "../../../../utils/viewport";

interface SliderProps {
  isMobile: boolean;
  style: any;
  isWidgetOpen: boolean;
  language: string;
  // The live conversation column rendered inside the fullscreen view
  // (built by Chat.tsx, which owns the chat state).
  chatPanel?: React.ReactNode;
  // Standalone page: the chat is a card centered in the viewport rather than
  // pinned bottom-right, so the slider docks against that centre instead.
  isStandalone?: boolean;
  // Where the open chat panel sits on desktop (from the configured launcher
  // position). Absent means the historical bottom-right, 20px.
  panelAnchor?: { side: "left" | "right"; bottom: number; offset: number };
}

// HM templates render their own chrome and never join the Esplora hub.
const HM_TEMPLATES = ["hm_hotel", "hm_offer"];

export const Slider = ({
  isMobile,
  isWidgetOpen,
  style,
  language,
  chatPanel,
  isStandalone = false,
  panelAnchor,
}: SliderProps) => {
  const sliderState = useSliderState();
  const bookingActive = useBookingState((s) => s.isActive);
  const t = localization.get(language);
  const currentType = sliderState.currentTemplate?.type;
  const isExpandable = isExpandableTemplate(sliderState.currentTemplate);
  const isHM = HM_TEMPLATES.includes(currentType);

  // On a phone the booking owns the docked sheet outright: it is a transaction,
  // not a card to browse next to other cards. So the sheet's own chrome steps
  // aside (the panel carries its own close) and the immersive overlay is never
  // raised for it.
  const bookingOwnsSheet = isMobile && bookingActive;

  // The booking form has fields, so the keyboard opens over this sheet too:
  // size it against the visible box like the chat panel, not the layout
  // viewport the keyboard slides over.
  const visibleBox = useVisualViewportBox(isMobile && isWidgetOpen);

  if (!isWidgetOpen) return null;

  const templates = sliderState?.templates ?? [];
  if (!templates.length) return null;

  const explorableCount = templates.filter(
    (template: any) => !HM_TEMPLATES.includes(template?.type)
  ).length;

  // Hierarchy: Esplora is a labelled pill (discovery), expand is a quiet
  // glass circle, close is the solid primary circle.
  const renderChrome = () =>
    !isHM &&
    !bookingOwnsSheet && (
      <div className="slider-chrome">
        {explorableCount > 1 && (
          <button
            className="slider-chrome-btn pill"
            onClick={() => sliderState.setExploreOpen(true)}
            aria-label={t.explore_title}
            title={t.explore_title}
          >
            <LayoutGrid className="icon" />
            <span>{t.explore}</span>
          </button>
        )}
        {/* Not on mobile: the chat panel is already 100% x 100% and this sheet
            is at 90%, so "fullscreen" bought about ten percent of height and
            cost the guest a control that looked like it did something. The
            immersive surface still exists on a phone, but only content reaches
            it (read more, a link in the conversation) — never a mode toggle. */}
        {isExpandable && !isMobile && (
          <button
            className="slider-chrome-btn"
            onClick={() => sliderState.setExpanded(true)}
            aria-label={t.expand_view}
            title={t.expand_view}
          >
            <Maximize2 className="icon" />
          </button>
        )}
        <button
          className="slider-chrome-btn primary"
          onClick={sliderState.closeSlider}
          aria-label={t.back_to_chat}
          title={t.back_to_chat}
        >
          <X className="icon" />
        </button>
      </div>
    );

  const getComponent = (template: any) => {
    switch (template?.type) {
      case "quote":
      case "list_views":
        return <ShowcaseCard template={template} language={language} />;
      case "map":
      case "map_point":
        return <PlacesTemplate template={template} language={language} />;
      case "poi_map":
        return <PoiMapTemplate template={template} language={language} />;
      case "itinerary":
        return <ItineraryTemplate template={template} language={language} />;
      case "videos":
        return <VideosTemplate template={template} language={language} />;
      case "hm_hotel":
        return <HotelSliderTemplate template={template} />;
      case "hm_offer":
        return <OfferSliderTemplate template={template} />;
      default:
        return null;
    }
  };

  const renderContent = () => {
    const template = sliderState.currentTemplate || templates[0] || null;
    const index = sliderState.subTemplateItemIndex || 0;

    // The booking takes the whole sheet on mobile. Keyed separately from the
    // template so entering and leaving the flow is its own transition, not a
    // template swap.
    if (bookingOwnsSheet) {
      return (
        <div className="slider-booking-host">
          <BookingPanel language={language} isMobile />
        </div>
      );
    }
    // Showcase and places handle item changes internally (card rise, image
    // blur-through); only the HM galleries still remount per item.
    const key = HM_TEMPLATES.includes(template?.type)
      ? `${template?.type}-${index}`
      : `${template?.type}-${template?.__key ?? ""}`;

    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          borderRadius: "inherit",
        }}
      >
        <AnimatePresence mode="sync">
          <motion.div
            key={key}
            initial={{ opacity: 0, scale: 1.045 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.3 } }}
            transition={{ duration: 0.5, ease: [0.3, 0.7, 0.3, 1] }}
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              borderRadius: "inherit",
              overflow: "hidden",
            }}
          >
            {getComponent(template)}
          </motion.div>
        </AnimatePresence>
      </div>
    );
  };

  const renderExplore = () => (
    <AnimatePresence>
      {sliderState.isExploreOpen && !isHM && (
        <ExploreDrawer language={language} />
      )}
    </AnimatePresence>
  );

  // A transaction gets the whole sheet. Browsing keeps the 90% peek that says
  // "there is a conversation behind this".
  const sheetShare = bookingOwnsSheet ? 1 : 0.9;
  const sheetGeometry: React.CSSProperties = visibleBox
    ? {
        top: `${visibleBox.top + visibleBox.height * (1 - sheetShare)}px`,
        left: `${visibleBox.left}px`,
        width: `${visibleBox.width}px`,
        height: `${visibleBox.height * sheetShare}px`,
        // Slides by transform, so the keyboard moving the box snaps the sheet
        // into place instead of replaying the open animation.
        transform: sliderState.isVisible ? "none" : "translateY(110%)",
        transition: "transform 0.4s ease-in-out, opacity 0.4s ease-in-out",
      }
    : {
        bottom: sliderState.isVisible ? "0%" : "-100%",
        left: 0,
        width: "100%",
        height: `${sheetShare * 100}%`,
        transition: "bottom 0.4s ease-in-out, opacity 0.4s ease-in-out",
      };

  const renderMobileSlider = () => (
    <div
      style={{
        position: "fixed",
        ...sheetGeometry,
        backgroundColor: "#fff",
        boxShadow: "0 14px 44px rgba(16, 11, 22, 0.24)",
        borderTopLeftRadius: "30px",
        borderTopRightRadius: "30px",
        opacity: sliderState.isVisible ? 1 : 0,
        pointerEvents: sliderState.isVisible ? "auto" : "none",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          position: "relative",
          height: "100%",
          width: "100%",
          borderRadius: "inherit",
          flex: 1,
          overflow: "hidden",
        }}
      >
        {/* Not during a booking: it closes the whole sheet, and losing a
            half-filled form to a stray thumb on the top edge is not a gesture
            anyone meant to make. The panel carries its own close. */}
        {!bookingOwnsSheet && (
          <div className="slider-grab-handle" onClick={sliderState.closeSlider} />
        )}
        {renderChrome()}
        {renderContent()}
        {renderExplore()}
      </div>
    </div>
  );

  // Both layouts are "tucked behind the chat when hidden, slide out to its
  // left when shown"; only the anchor differs. Standalone measures from the
  // viewport centre, so it needs no shared container with the chat card.
  const desktopGeometry: React.CSSProperties = isStandalone
    ? {
        top: "50%",
        transform: "translateY(-50%)",
        right: sliderState.isVisible
          ? `calc(50% + ${constants.DIMENSIONS.STANDALONE.HALF_STRING})`
          : `calc(50% - ${constants.DIMENSIONS.STANDALONE.HALF_STRING})`,
        height: constants.DIMENSIONS.STANDALONE.HEIGHT_STRING,
        borderRadius: "28px 0px 0px 28px",
      }
    : (() => {
        const anchor = panelAnchor ?? { side: "right", bottom: 20, offset: 20 };
        const dockedLeft = anchor.side === "left";
        const raised = anchor.bottom > 20;
        return {
          bottom: `${anchor.bottom}px`,
          // Tucked behind the chat when hidden, out on its inner side when
          // shown: to the left of a right-hand chat, to the right of a
          // left-hand one.
          [dockedLeft ? "left" : "right"]: !sliderState.isVisible
            ? `${anchor.offset}px`
            : `${anchor.offset + constants.DIMENSIONS.DESKTOP.OPEN.WIDTH}px`,
          // Same height rule as the chat panel it docks against.
          height: raised
            ? `min(${constants.DIMENSIONS.DESKTOP.OPEN.HEIGHT_STRING}, calc(100vh - ${
                anchor.bottom + 20
              }px))`
            : constants.DIMENSIONS.DESKTOP.OPEN.HEIGHT_STRING,
          borderRadius: dockedLeft ? "0px 30px 30px 0px" : "30px 0px 0px 30px",
        };
      })();

  const renderDesktopSlider = () => (
    <div
      style={{
        position: "fixed",
        width: constants.DIMENSIONS.DESKTOP.OPEN.WIDTH_STRING,
        boxShadow: "0 14px 44px rgba(16, 11, 22, 0.24)",
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        opacity: sliderState.isVisible ? 1 : 0,
        pointerEvents: sliderState.isVisible ? "auto" : "none",
        transition:
          "right 0.4s ease-in-out, left 0.4s ease-in-out, opacity 0.3s ease-in-out 0.1s, border 0.3s ease-in-out",
        ...desktopGeometry,
      }}
    >
      <div
        style={{
          position: "relative",
          flex: 1,
          borderRadius: "inherit",
          overflow: "hidden",
        }}
      >
        {renderChrome()}
        {renderContent()}
        {renderExplore()}
      </div>
    </div>
  );

  return (
    <>
      {isMobile ? renderMobileSlider() : renderDesktopSlider()}
      <AnimatePresence>
        {sliderState.isExpanded && isExpandable && !bookingOwnsSheet && (
          <ImmersiveView
            language={language}
            isMobile={isMobile}
            chatPanel={!isMobile ? chatPanel : undefined}
          />
        )}
      </AnimatePresence>
    </>
  );
};
